import { Component, Vue, Prop, Inject, Watch, Emit } from 'vue-property-decorator';
import AchEntryForm from './AchEntryForm.vue';
import WarningAlert from '../../components/errors/WarningAlert.vue';
import { default as AchEntryFormClass } from './AchEntryFormVue';
import FieldValidationErrors from '../../components/errors/FieldValidationErrors.vue';
import { PropOptions } from 'vue';
import { createPaymentProcessorOptions, PaymentProcessorOptions, PaymentContainerToken, createPaymentContainerToken, InvoiceTokenData, CreditCardTokenData, PaymentTypeEnum, AchTokenData, createAchTokenData, BasicSelectListItem, createBasicSelectListItem, ApiErrorResponse, AutoPayOption, PaymentProcessorEnum } from '@/common/types/AllTypesExports';
import { paymentsRepo } from '../../common/repositories/PaymentsRepo';
import { AxiosResponse } from 'axios';
import CustomerSelector from '../membership/CustomerSelector.vue';
import { INoviNsPaymentGlobals } from '../../common/types/Payment/NoviNsPaymentGlobals';
import { SavedAch } from '../../common/types/Payment/SavedAch';
import { DtoCurrentUser } from '../../common/types/user/DtoCurrentUser';
import StripePaymentForm from './StripePaymentForm.vue';
import StripePaymentFormClass from './StripePaymentFormVue';
import FiservAchEntryForm from './FiservAchEntryForm.vue';
import FiservAchEntryFormClass from './FiservAchEntryFormVue';

declare let noviNs: INoviNsPaymentGlobals;

@Component({
    name: 'achContainer',
    components: {
        AchEntryForm,
        FieldValidationErrors,
        WarningAlert,
        CustomerSelector,
        StripePaymentForm,
        FiservAchEntryForm
    }
})
export default class AchContainerClass extends Vue {

    @Prop({ required: true } as PropOptions<PaymentProcessorOptions>)
    processorOptions!: PaymentProcessorOptions;

    @Prop({ required: false, default: () => true })
    achActive!: boolean;

    @Prop({ required: false, default: () => false })
    forceSaveAch!: boolean;

    @Prop({ required: false, default: () => false })
    locked!: boolean;

    @Prop({ required: false, default: () => "" })
    achRestrictionMessage!: string;

    @Prop({ required: false, default: () => [] })
    autoPayOptions!: AutoPayOption[];

    @Prop({ required: false, default: false })
    setMemberTypeDefaultBillableParty: boolean | undefined;

    @Prop({ required: false }) readonly currentUser!: DtoCurrentUser;

    @Prop({ required: false, default: () => false })
    achBlockedByPaymentLimit!: boolean;

    @Prop({ required: false, default: () => "" })
    paymentLimitMessage!: string;

    storedBankAccountId: string = "";
    bankAccountPaymentProcessorModule: PaymentProcessorEnum | null = null;
    newAchSelected: boolean = false;
    billableParties: BasicSelectListItem[] = [];
    billableParty = createBasicSelectListItem();
    errorMessage: string = '';
    localWarningMessage: string = '';
    isCheckingForBillableParties: boolean = false;
    noBillablePartiesMessage: string = 'No eligible billable parties found for ACH. If you believe this to be an error, please contact us.';

    achTokenData = createAchTokenData(<AchTokenData>{
        storedBankAccountId: this.storedBankAccountId,
        last4Digits: this.storedBankAccountId.substr(-4),
        billablePartyId: this.processorOptions.billablePartyId
    });

    @Inject('validator')
    $validator: any;

    truncateName(name: string) {
        return (name.length > 30) ? name.substr(0, 29) + '...' : name;
    }

    get preventAchUsageMessage() {
        return this.localWarningMessage || '';
    }

    get preventAchUsage() {
        return !!this.preventAchUsageMessage.length;
    }

    get showSavedAchs() {
        if (this.processorOptions && this.processorOptions.savedAchs) {
            return this.processorOptions.enableSaveAch && this.processorOptions.savedAchs.length > 0;
        }
        return false;
    }

    get isForcedSaveACH() {
        return this.forceSaveAch || this.autoPayOptions.filter(x => x.enabled).length > 0;
    }

    get qbPaymentProcessor(): boolean {
        return noviNs.paymentProcessor == PaymentProcessorEnum.quickBooksPayments;
    }

    get disableCurrentUserAutoPay() {
        return this.autoPayOptions.filter(x => x.isCurrentUser).length == 0 || this.processorOptions.autoPayRequired;
    }

    get autoPayOptionsOrdered() {
        return this.autoPayOptions.sort((a, b) => Number(b.isCurrentUser) - Number(a.isCurrentUser))
    }

    get showAutoPay() {
        return this.processorOptions.enableAutoPay && this.autoPayOptions.length > 0 && (this.bankAccountPaymentProcessorModule == noviNs.paymentProcessor || !this.processorOptions.noviPayUpdatePaymentMethodsEnabled);
    }

    get showAddACH() {
        return noviNs.paymentProcessor != PaymentProcessorEnum.stripe;
    }

    get useQBOEmbed() {
        return noviNs.paymentProcessor == PaymentProcessorEnum.quickBooksPayments;
    }

    get useStripeEmbed() {
        return noviNs.paymentProcessor == PaymentProcessorEnum.noviPay || noviNs.paymentProcessor == PaymentProcessorEnum.stripe;
    }

    get useFiservEmbed() {
        return noviNs.paymentProcessor == PaymentProcessorEnum.fiserv;
    }

    get autoPayOptionUpdated() {
        return this.autoPayOptions.findIndex(option => option.enabled) >= 0;
    }

    get showAutoPayHelperText() {
        return noviNs.autoPayInstantOnOptIn && this.processorOptions.includesOpenDuesInvoice && this.autoPayOptionUpdated;
    }

    get showSimpleAchForm() {
        return noviNs.paymentProcessor == PaymentProcessorEnum.quickBooksPayments;
    }

    expandNewAchEntry() {
        if (!this.locked) {
            this.storedBankAccountId = "";
            this.newAchSelected = true;
            this.bankAccountPaymentProcessorModule = noviNs.paymentProcessor;
        }
    }

    selectStoredAch(selectedBankAccount: SavedAch) {
        if (!this.locked) {
            this.newAchSelected = false;
            this.storedBankAccountId = selectedBankAccount.id;
            this.bankAccountPaymentProcessorModule = selectedBankAccount.paymentProcessorModule;
        }
    }

    @Emit('ach-token-updated')
    emitAchTokenUpdate() {
        return this.achTokenData;
    }

    @Watch('newAchSelected', { immediate: true })
    onNewCardSelectedChange(value: boolean) {
        this.$emit('update:is-new-ach-selected', value);
    }

    @Watch('currentUser', { immediate: true })
    onCurrentUserChanged(newVal: DtoCurrentUser | null | undefined) {
        if (newVal === undefined || newVal === null) {
            return; // currentUser hasn't been set yet
        }

        if (!newVal.isLoggedIn) {
            this.localWarningMessage = 'You must login in order to be eligible for ACH.';
        }
    }

    @Watch('billableParty')
    onBillablePartyChange(newBillableParty: BasicSelectListItem) {
        Object.assign(this.billableParty, newBillableParty);
        this.achTokenData.billablePartyId = this.billableParty.id;
        this.achTokenData.hasPaymentAddress = this.billableParty.hasPaymentAddress;
        this.emitAchTokenUpdate();
    }

    // Watch for external changes to billablePartyId (change purchaser)
    @Watch('processorOptions.billablePartyId')
    onBillablePartyIdChange(newBillablePartyId: number) {
        if (isNaN(newBillablePartyId)) {
            this.updateBillToDropdownSelector(null);
        }
        else if (newBillablePartyId) {
            this.isCheckingForBillableParties = true;
            paymentsRepo.getBillableParties('SalesReceipt', newBillablePartyId, this.setMemberTypeDefaultBillableParty)
                .then((billableCustomers: BasicSelectListItem[]) => {
                    this.billableParties.splice(0, this.billableParties.length, ...billableCustomers);
                    this.errorMessage = '';
                    this.localWarningMessage = '';

                    if (this.billableParties.length) {
                        const newBillableParty = this.billableParties.find(b => b.id === newBillablePartyId) ?? this.billableParties[0];
                        Object.assign(this.billableParty, newBillableParty);
                        this.onBillablePartyChange(this.billableParty);
                        this.updateBillToDropdownSelector(this.billableParty);
                    }
                    else {
                        this.localWarningMessage = this.noBillablePartiesMessage;
                    }

                    this.onIsAchProcessingPrevented(this.preventAchUsage);
                })
                .catch((error: any) => {
                    // checking if manual retry exists
                    if (error.manualRetry) {
                        this.errorMessage = error.message;
                    } else {
                        this.errorMessage = error.data?.message || "Error loading billable parties";
                    }
                })
                .finally(() => {
                    this.isCheckingForBillableParties = false;
                });
        }
    }

    onIsAchProcessingPrevented(value: boolean) {
        this.$emit('on-is-ach-processing-prevented', value);
    }

    onAchPaymentTokenReady(card: AchTokenData) {
        this.$emit('on-ach-token-ready', card);
    }

    onErrorToggle(hasError: boolean, errorMsg: string) {
        this.$emit('on-error-toggle', hasError, errorMsg);
    }

    createPaymentToken() {
        if (this.storedBankAccountId) {
            var savedAchToken = createAchTokenData(<AchTokenData>{
                storedBankAccountId: this.storedBankAccountId,
                autoPayOptions: this.autoPayOptions,
                last4Digits: this.storedBankAccountId.substr(-4),
                billablePartyId: this.billableParty.id,
                paymentProcessorModule: this.bankAccountPaymentProcessorModule,
                transactionNotes: this.achTokenData.transactionNotes
            });
            this.onAchPaymentTokenReady(savedAchToken);
        }
        else if (this.useStripeEmbed) {
            const stripeEmbed = <StripePaymentFormClass>this.$refs.achStripeEmbed;
            stripeEmbed.createStripePaymentToken(this.autoPayOptions, this.billableParty.id);
        }
        else if (this.showSimpleAchForm) {
            const achEntryContainer = <AchEntryFormClass>this.$refs.achEntryForm;
            achEntryContainer.createPaymentToken(this.autoPayOptions, this.billableParty.id);
        }
        else if (this.useFiservEmbed) {
            const achFiservContainer = <FiservAchEntryFormClass>this.$refs.achFiservEmbed;
            achFiservContainer.createPaymentToken(this.autoPayOptions, this.billableParty.id);
        }
    }

    mounted() {
        if (this.achTokenData.billablePartyId) {
            this.isCheckingForBillableParties = true;
            paymentsRepo.getBillableParties('SalesReceipt', this.processorOptions.customerForBillableParties ?? this.achTokenData.billablePartyId, this.setMemberTypeDefaultBillableParty)
                .then((billableCustomers: BasicSelectListItem[]) => {
                    this.billableParties.splice(0, this.billableParties.length, ...billableCustomers);
                    this.errorMessage = '';
                    this.localWarningMessage = '';

                    if (this.billableParties.length) {
                        const currentBillableParty = this.billableParties.find(b => b.isDefault) ?? this.billableParties[0];
                        Object.assign(this.billableParty, currentBillableParty);
                        this.onBillablePartyChange(this.billableParty);
                        this.updateBillToDropdownSelector(this.billableParty);
                    }
                    else {
                        this.localWarningMessage = this.noBillablePartiesMessage;
                    }

                    this.onIsAchProcessingPrevented(this.preventAchUsage);
                })
                .catch((error: any) => {
                    if (error.manualRetry) {
                        this.errorMessage = error.message;
                    } else {
                        this.errorMessage = error.data?.message || "Error loading billable parties";
                    }
                })
                .finally(() => {
                    this.isCheckingForBillableParties = false;
                });
        }

        if (this.processorOptions.enableACH && this.processorOptions.savedAchs && this.processorOptions.savedAchs.length > 0) {
            this.storedBankAccountId = this.processorOptions.savedAchs.length ? this.processorOptions.savedAchs[0].id : '';
            this.bankAccountPaymentProcessorModule = this.processorOptions.savedAchs.length ? this.processorOptions.savedAchs[0].paymentProcessorModule : null;
        } else {
            this.bankAccountPaymentProcessorModule = noviNs.paymentProcessor;
        }
    }

    // Update the 'Bill To' drop down value if changed from outside the vue app
    updateBillToDropdownSelector(value: any) {
        const customerSelector = this.$refs.billablePartySelector as any;
        if (customerSelector && typeof customerSelector.setSelectedCustomer === 'function') {
            customerSelector.setSelectedCustomer(value);
        }
    }

    onGuestDataUpdated(name: string, guestName: boolean) {
        const index = guestName ? this.billableParties.findIndex(item => item.id == -1) : this.billableParties.findIndex(item => item.id == -2);

        if (index === -1) {
            if (guestName) {
                this.billableParties.push({
                    id: -1,
                    displayName: name,
                    taxable: true,
                    isDefault: false,
                    hasPaymentAddress: false
                });
            }
            else {
                this.billableParties.push({
                    id: -2,
                    displayName: name,
                    taxable: true,
                    isDefault: false,
                    hasPaymentAddress: false
                });
            }
        } else {
            this.billableParties[index].displayName = name;
        }
        if (this.billableParties.length === 1) {
            const newBillableParty = this.billableParties[0];
            Object.assign(this.billableParty, newBillableParty);
            this.onBillablePartyChange(this.billableParty);
            this.updateBillToDropdownSelector(this.billableParty);
        }
    }

    retryBillableParties() {
        this.errorMessage = "";
        let bpId = this.processorOptions.customerForBillableParties ?? this.achTokenData.billablePartyId;
        this.isCheckingForBillableParties = true;
        paymentsRepo.getBillableParties('SalesReceipt', bpId, this.setMemberTypeDefaultBillableParty)
            .then((billableCustomers: BasicSelectListItem[]) => {
                this.billableParties.splice(0, this.billableParties.length, ...billableCustomers);
                this.errorMessage = '';
                this.localWarningMessage = '';

                if (this.billableParties.length) {
                    const selected = this.billableParties.find(b => b.id === bpId) ?? this.billableParties[0];
                    Object.assign(this.billableParty, selected);
                    this.onBillablePartyChange(this.billableParty);
                    this.updateBillToDropdownSelector(this.billableParty);
                } else {
                    this.localWarningMessage = this.noBillablePartiesMessage;
                }
            })
            .catch((error: any) => {
                this.errorMessage = error.data?.message || "Error loading billable parties";
                this.fallbackToPurchaser();
            })
            .finally(() => {
                this.isCheckingForBillableParties = false;
            });
    }

    fallbackToPurchaser() {
        const purchaserId = this.currentUser.customerID || 0;
        const purchaserName = this.currentUser.firstAndLastName || "Purchaser";
        const taxable = this.currentUser.taxable;

        if (purchaserId && purchaserId !== 0) {
            const fallbackParty: BasicSelectListItem = {
                id: purchaserId,
                displayName: purchaserName,
                taxable: taxable,
                isDefault: true,
                hasPaymentAddress: false
            };

            Object.assign(this.billableParty, fallbackParty);
            this.achTokenData.billablePartyId = purchaserId;
            this.achTokenData.transactionNotes = "Billable Party Fallback";
            this.onBillablePartyChange(this.billableParty);
            this.updateBillToDropdownSelector(this.billableParty);
            this.billableParties.push(fallbackParty);
            this.errorMessage = "";
        } else {
            this.errorMessage = this.errorMessage || "Error loading billable parties.";
        }
    }
}